import React from 'react';
import _ from 'lodash';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from '@material-ui/core';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import { resetTooltipStyle } from 'components/Tooltips/helpers';
import TooltipWrapper from 'components/Tooltips/TooltipWrapper';
import { Flex, Margin } from 'components/base/layout-components';
import Text from 'components/base/Text';
import MultipleAvatars from 'components/MultipleAvatars';
import { TwoCircles } from './TwoCircles';
import moment from 'moment';
import { getTimeAgoFormat } from 'common/utils/utils';
import { TRACKING, TIME_PICKER_FORMAT } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import { getBranchItems } from '../../pulse.service';

const TooltipTitle = styled(Flex)`
  padding: 1.5rem 1.5rem 1rem;
  align-items: center;
  border-bottom: 1px solid ${getColor('catskillWhite')};
`;
const TodayChipWrapper = styled(Flex)`
  align-items: center;
  background: #eef0ff;
  padding: 0.2rem 0.5rem;
  border-radius: 0.2rem;
`;
const TodayChip = () => (
  <TodayChipWrapper>
    <Text size='x-small' color='shark'>
      TODAY
    </Text>
  </TodayChipWrapper>
);

const TooltipItemWrapper = styled(Flex)`
  padding: 1.5rem;
  align-items: flex-start;
  justify-content: space-between;
  width: 32rem;
`;
const TimestampWrapper = styled(Flex)`
  margin-top: 0.2rem;
  padding: 0.2rem 0.4rem;
  align-items: center;

  ${({ justifyContent }) =>
    justifyContent
      ? `justify-content: ${justifyContent};`
      : 'justify-content: center;'}
  border-radius: 0.3rem;
  ${({ highlighted }) =>
    highlighted
      ? `background:${getColor('fetaLight')}; 1px solid ${getColor('feta')};`
      : ''}
`;

const BranchTooltipItem = ({
  circles,
  title,
  pullRequestUrl,
  avatars,
  isToday,
  selectedContributorIds,
  ...rest
}) => {
  return (
    <TooltipItemWrapper>
      <Flex>
        <Flex mr='small'>
          <TwoCircles circles={circles} />
        </Flex>
        <Flex direction='column' mr='small'>
          <Text size='medium' color='shark' pb='0.3rem'>
            {title}
          </Text>
          <TimestampWrapper
            highlighted={rest.highlightedTimestamp}
            justifyContent='start'
          >
            <Text color={rest.highlightedTimestamp ? 'funGreen' : 'osloGray'}>
              {isToday
                ? getTimeAgoFormat(rest.timestamp)
                : moment(rest.timestamp).format(TIME_PICKER_FORMAT)}
            </Text>
          </TimestampWrapper>
        </Flex>
      </Flex>
      <MultipleAvatars avatars={avatars} />
    </TooltipItemWrapper>
  );
};

const TooltipItem = ({
  circles,
  title,
  pullRequestUrl,
  subtitleItems = [],
  avatars,
  isToday,
  ...rest
}) => {
  return (
    <TooltipItemWrapper {...rest}>
      <Flex>
        <Flex mr='small'>
          <TwoCircles circles={circles} />
        </Flex>
        <Flex direction='column' mr='small'>
          <Text size='medium' color='shark' pb='0.3rem'>
            {pullRequestUrl ? (
              <Link
                onClick={() => {
                  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                    page: TRACKING.PAGES.PULSE,
                    click_type: TRACKING.CLICK.TIMELINE_PR_GIT_LINK
                  });
                }}
                href={pullRequestUrl}
                target='_blank'
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </Text>
          {subtitleItems.map((subtitleItem, index) => (
            <Flex
              key={index}
              mb={index === subtitleItems.length - 1 ? '' : 'tiny'}
              items='baseline'
            >
              <Text pr='0.5rem' size='medium' color='shuttleGray'>
                {subtitleItem.text}
              </Text>
              {((isToday && index === 0) ||
                subtitleItem.highlightedTimestamp) && (
                <TimestampWrapper
                  highlighted={subtitleItem.highlightedTimestamp}
                >
                  <Text
                    color={
                      subtitleItem.highlightedTimestamp
                        ? 'funGreen'
                        : 'osloGray'
                    }
                  >
                    {isToday
                      ? getTimeAgoFormat(subtitleItem.timestamp)
                      : moment(subtitleItem.timestamp).format(
                          TIME_PICKER_FORMAT
                        )}
                  </Text>
                </TimestampWrapper>
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
      <MultipleAvatars avatars={avatars} />
    </TooltipItemWrapper>
  );
};

const BranchGraphItemTooltipContent = ({
  title,
  items,
  isToday,
  selectedContributorIds
}) => {
  const branchTimeline = _.groupBy(items[0].timeline, 'type');

  return (
    <Flex direction='column'>
      <TooltipTitle>
        <Margin r='small'>
          <Text size='large' color='shark' weight='500'>
            {title}
          </Text>
        </Margin>
        {isToday && <TodayChip />}
      </TooltipTitle>
      <Flex direction='column'>
        {_.orderBy(
          getBranchItems(branchTimeline, selectedContributorIds),
          ['timestamp'],
          ['asc']
        ).map((item, idx) => (
          <BranchTooltipItem
            {...item}
            key={idx}
            selectedContributorIds={selectedContributorIds}
            isToday={isToday}
            style={
              idx !== items.length - 1
                ? { borderBottom: `1px solid ${getColor('catskillWhite')}` }
                : {}
            }
          />
        ))}
      </Flex>
    </Flex>
  );
};

const GraphItemTooltipContent = ({ title, items, isToday }) => {
  return (
    <Flex direction='column'>
      <TooltipTitle>
        <Margin r='small'>
          <Text size='large' color='shark' weight='500'>
            {title}
          </Text>
        </Margin>
        {isToday && <TodayChip />}
      </TooltipTitle>
      <Flex direction='column'>
        {_.orderBy(items, ['timestamp'], ['desc']).map((item, idx) => (
          <TooltipItem
            {...item}
            key={idx}
            isToday={isToday}
            style={
              idx !== items.length - 1
                ? { borderBottom: `1px solid ${getColor('catskillWhite')}` }
                : {}
            }
          />
        ))}
      </Flex>
    </Flex>
  );
};

const GraphItemWrapper = styled(Flex)`
  height: 100%;
  align-items: center;
  ${({ showAlertsStyle }) =>
    showAlertsStyle
      ? `
    height: 4.8rem;
    background-color: ${getColor('serenade')};
  `
      : ''}
  &:hover {
    ${({ showHoverStyle, showAlertsStyle }) =>
      showHoverStyle
        ? `
      background-color: ${getColor(
        showAlertsStyle ? 'serenade' : 'catskillGray'
      )};`
        : ''}
  }
`;
export const GraphItem = ({
  circles = [],
  tooltipData = [],
  hasAlerts,
  isStoryGraph,
  selectedContributorIds
}) => {
  const { title, isToday, items } = tooltipData;

  const Circles = (
    <GraphItemWrapper
      showAlertsStyle={hasAlerts}
      showHoverStyle={circles.length > 0}
    >
      {_.isEmpty(tooltipData) ? (
        <TwoCircles circles={circles} />
      ) : (
        <Tooltip
          classes={resetTooltipStyle()}
          onOpen={_.once(() => {
            TrackingService.logEvent(TRACKING.EVENTS.HOVER, {
              page: TRACKING.PAGES.PULSE,
              element_name: isStoryGraph
                ? TRACKING.ELEMENT_NAME.STORY_TIMELINE
                : TRACKING.ELEMENT_NAME.BRANCH_TIMELINE
            });
          })}
          interactive
          placement={'top-start'}
          title={
            <TooltipWrapper>
              {isStoryGraph ? (
                <GraphItemTooltipContent
                  items={items}
                  title={title}
                  isToday={isToday}
                />
              ) : (
                <BranchGraphItemTooltipContent
                  items={items}
                  title={title}
                  isToday={isToday}
                  selectedContributorIds={selectedContributorIds}
                />
              )}
            </TooltipWrapper>
          }
        >
          <div>
            <TwoCircles circles={circles} />
          </div>
        </Tooltip>
      )}
    </GraphItemWrapper>
  );

  return Circles;
};
